import { CircleAlert } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createRoot } from "react-dom/client";

import { Button } from "core/shadcn/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "core/shadcn/components/ui/dialog";
import { cn } from "core/shadcn/lib/utils";

const TITLE_ROW_CLASS_NAME =
  "flex items-center gap-2 border-b border-border py-3.5 pr-12 pl-4 text-sm font-semibold text-foreground";

type Focusable = {
  focus: (options?: FocusOptions) => void;
};

export interface ModalProps {
  open: boolean;
  /** Called on the close button, a click on the backdrop and Escape. */
  onClose?: () => void;
  title?: ReactNode;
  /** Controls at the end of the title row. They sit outside the title, so
   * they are not part of the dialog's accessible name. */
  titleActions?: ReactNode;
  /** Names the dialog for screen readers when there is no visible title. */
  "aria-label"?: string;
  footer?: ReactNode;
  children?: ReactNode;
  /** Show the close button in the corner. */
  closable?: boolean;
  /** Close when the backdrop is clicked. */
  maskClosable?: boolean;
  /** Close on Escape. */
  keyboard?: boolean;
  /** Classes for the dialog box, for example its width. Defaults to `w-96`. */
  className?: string;
  bodyClassName?: string;
  footerClassName?: string;
  titleClassName?: string;
  /** Field to focus on open. autoFocus alone loses when a menu still holds focus. */
  initialFocus?: RefObject<Focusable | null>;
  /** Runs after the modal has closed and focus has gone back to the opener. */
  afterClose?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  "data-test"?: string;
}

export function Modal({
  open,
  onClose,
  title,
  titleActions,
  "aria-label": ariaLabel,
  footer,
  children,
  closable = true,
  maskClosable = true,
  keyboard = true,
  className,
  bodyClassName,
  footerClassName,
  titleClassName,
  initialFocus,
  afterClose,
  onKeyDown,
  "data-test": dataTest,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose?.()}>
      <DialogContent
        data-test={dataTest}
        aria-describedby={undefined}
        showCloseButton={closable}
        onPointerDownOutside={(event) =>
          !maskClosable && event.preventDefault()
        }
        onEscapeKeyDown={(event) => !keyboard && event.preventDefault()}
        onKeyDown={onKeyDown}
        // The dialog content returns focus in this same handler, so wait for it.
        onCloseAutoFocus={() => afterClose && queueMicrotask(afterClose)}
        // Radix calls this only when focus is still outside the dialog, for
        // example on the menu item that opened it, so a field's autoFocus has
        // lost. Focus initialFocus if given, else the dialog, not its first button.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const field = initialFocus?.current;
          if (field) {
            field.focus({ preventScroll: true });
            return;
          }
          if (event.target instanceof HTMLElement) {
            event.target.focus({ preventScroll: true });
          }
        }}
        className={cn(
          "flex w-96 flex-col gap-4 rounded-md p-0 text-xs text-subtle-foreground",
          className,
        )}
      >
        {title && titleActions ? (
          <div className={cn(TITLE_ROW_CLASS_NAME, titleClassName)}>
            {/* `!` because antd's unlayered heading color beats the row's. */}
            <DialogTitle className="!mb-0 min-w-0 flex-1 truncate text-sm !text-foreground">
              {title}
            </DialogTitle>
            {titleActions}
          </div>
        ) : title ? (
          <DialogTitle className={cn(TITLE_ROW_CLASS_NAME, titleClassName)}>
            {title}
          </DialogTitle>
        ) : (
          <DialogTitle className="sr-only">{ariaLabel}</DialogTitle>
        )}
        <div
          className={cn(
            "flex flex-col px-4",
            !footer && "pb-4",
            !title && "pt-4",
            bodyClassName,
          )}
        >
          {children}
        </div>
        {footer && (
          <div
            className={cn("flex justify-end gap-3 px-4 pb-4", footerClassName)}
          >
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DeleteModalProps extends Omit<ModalProps, "footer" | "onClose"> {
  cancelButtonText?: string;
  dataTestName?: string;
  deleteButtonText?: string;
  disableDelete?: boolean;
  error?: string;
  isDeleting: boolean;
  onCancel: () => void;
  onDelete: () => void;
}

export function DeleteModal({
  cancelButtonText = "Cancel",
  children,
  dataTestName = "",
  deleteButtonText = "Delete",
  disableDelete,
  error,
  isDeleting,
  onCancel,
  onDelete,
  title,
  ...props
}: DeleteModalProps) {
  // Enter confirms, once per opening, unless it comes from a field or a
  // button that handles Enter itself.
  const hasConfirmedRef = useRef(false);
  useEffect(() => {
    if (props.open) hasConfirmedRef.current = false;
  }, [props.open]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" || event.repeat || hasConfirmedRef.current) {
      return;
    }
    if (disableDelete || isDeleting) return;
    const { target } = event;
    if (
      target instanceof HTMLElement &&
      (target.isContentEditable ||
        target.closest("input, textarea, select, button, a"))
    ) {
      return;
    }
    hasConfirmedRef.current = true;
    onDelete();
  };

  return (
    <Modal
      {...props}
      onClose={onCancel}
      onKeyDown={handleKeyDown}
      title={
        <>
          <CircleAlert className="size-5 shrink-0 text-sb-red-500" />
          {title}
        </>
      }
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isDeleting}
            data-test={`cancel-delete-${dataTestName}`}
          >
            {cancelButtonText}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting || disableDelete}
            data-test={`confirm-delete-${dataTestName}`}
          >
            {deleteButtonText}
          </Button>
        </>
      }
    >
      <div className="text-sm leading-5 [&_b]:font-medium">{children}</div>
      {error && <div className="text-sb-red-500">{error}</div>}
    </Modal>
  );
}

export interface ConfirmOptions {
  title: ReactNode;
  content?: ReactNode;
  /** Shown before the title. */
  icon?: ReactNode;
  okText?: string;
  cancelText?: string;
  /** Red confirm button, for actions that remove or delete something. */
  danger?: boolean;
  /** When it returns a promise, the buttons stay disabled until it settles.
   * The dialog closes when it resolves and stays open when it rejects. */
  onOk?: () => unknown;
  /** Runs on the cancel button and on Escape. */
  onCancel?: () => void;
  /** Runs after the dialog has closed and focus has gone back to the opener. */
  afterClose?: () => void;
}

export interface ConfirmHandle {
  update: (options: Partial<ConfirmOptions>) => void;
  /** Closes the dialog without running onOk or onCancel. */
  destroy: () => void;
}

function ConfirmDialog({
  open,
  close,
  title,
  content,
  icon,
  okText = "OK",
  cancelText = "Cancel",
  danger,
  onOk,
  onCancel,
  afterClose,
}: ConfirmOptions & { open: boolean; close: () => void }) {
  const okRef = useRef<HTMLButtonElement>(null);
  const [pending, setPending] = useState(false);
  // Set on the first click, so a double click or a click during the closing
  // animation does not run onOk or onCancel again.
  const answeredRef = useRef(false);
  // React can batch confirm() and an immediate destroy() into one render with
  // open false. The dialog then never shows, so its close handler never runs
  // afterClose, which is what removes the confirm from the page. Run it here.
  const shownRef = useRef(open);
  if (open) shownRef.current = true;
  useEffect(() => {
    if (!open && !shownRef.current) queueMicrotask(() => afterClose?.());
  }, [open, afterClose]);

  const cancel = () => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    onCancel?.();
    close();
  };
  const ok = async () => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    setPending(true);
    try {
      await onOk?.();
      close();
    } catch (error) {
      answeredRef.current = false;
      setPending(false);
      throw error;
    }
  };

  return (
    <Modal
      open={open}
      onClose={cancel}
      afterClose={afterClose}
      closable={false}
      maskClosable={false}
      keyboard={!pending}
      // Focus on the confirm button, so Enter confirms.
      initialFocus={okRef}
      title={
        <>
          {icon}
          {title}
        </>
      }
      bodyClassName={content ? undefined : "hidden"}
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={cancel}
            disabled={pending}
          >
            {cancelText}
          </Button>
          <Button
            ref={okRef}
            variant={danger ? "destructive" : "default"}
            size="sm"
            onClick={ok}
            disabled={pending}
          >
            {okText}
          </Button>
        </>
      }
    >
      <div className="text-sm leading-5 break-words">{content}</div>
    </Modal>
  );
}

/**
 * Asks the user to confirm an action. It renders in its own root, so sagas,
 * hooks and click handlers can call it and the dialog outlives the component
 * that opened it.
 */
export function confirm(options: ConfirmOptions): ConfirmHandle {
  const host = document.body.appendChild(document.createElement("div"));
  const root = createRoot(host);
  let props = options;
  let open = true;
  let mounted = true;

  const render = () => {
    if (!mounted) return;
    root.render(
      <ConfirmDialog
        {...props}
        open={open}
        close={destroy}
        afterClose={() => {
          if (!mounted) return;
          props.afterClose?.();
          mounted = false;
          root.unmount();
          host.remove();
        }}
      />,
    );
  };
  function destroy() {
    open = false;
    render();
  }

  render();
  return {
    update: (patch) => {
      props = { ...props, ...patch };
      render();
    },
    destroy,
  };
}
